import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiHandle from "services/apiHandle";

export const initialState = {
  services: [],
  categories: [],
  currentService: null,
  loading: false,
  error: null,
  message: "",
  showMessage: false,
};

// Fetch all categories
export const fetchCategories = createAsyncThunk(
  "services/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/admin/categories/getall", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch categories");
    }
  }
);

// Add new service
export const addService = createAsyncThunk(
  "services/addService",
  async (serviceData, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/services/add-service", "POST", serviceData);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to add service");
    }
  }
);

// Fetch services
export const fetchServices = createAsyncThunk(
  "services/fetchServices",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiHandle("/services/my-services", "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch services");
    }
  }
);

// Fetch service by ID
export const fetchServiceById = createAsyncThunk(
  "services/fetchServiceById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/services/getbyid/${id}`, "GET");
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch service details");
    }
  }
);

// Update service
export const updateService = createAsyncThunk(
  "services/updateService",
  async ({ id, serviceData }, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/services/update/${id}`, "PUT", serviceData);
      return res;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update service");
    }
  }
);

// Delete service
export const deleteService = createAsyncThunk(
  "services/deleteService",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiHandle(`/services/delete/${id}`, "DELETE");
      return { id, ...res };
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete service");
    }
  }
);

export const servicesSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = "";
      state.showMessage = false;
      state.error = null;
    },
    setCurrentService: (state, action) => {
      state.currentService = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data?.categories || [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Service
      .addCase(addService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addService.fulfilled, (state, action) => {
        state.loading = false;
        state.services.push(action.payload.data);
        state.message = action.payload.message || "Service added successfully!";
        state.showMessage = true;
      })
      .addCase(addService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Fetch Services
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload.data || [];
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Service By ID
      .addCase(fetchServiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentService = action.payload.data;
      })
      .addCase(fetchServiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Service
      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.services.findIndex(
          (s) => s._id === action.payload.data._id
        );
        if (index !== -1) {
          state.services[index] = action.payload.data;
        }
        state.message = action.payload.message || "Service updated successfully!";
        state.showMessage = true;
      })
      .addCase(updateService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Delete Service
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.loading = false;
        state.services = state.services.filter((s) => s._id !== action.payload.id);
        state.message = action.payload.message || "Service deleted successfully!";
        state.showMessage = true;
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.message = action.payload;
        state.showMessage = true;
      });
  },
});

export const { clearMessage, setCurrentService } = servicesSlice.actions;

export default servicesSlice.reducer;
